import React, { useState, useEffect } from "react";
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Upload, X, Plus } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';

const PRELOADED_AMENITIES = [
    "WiFi",
    "Parking",
    "Pool",
    "Gym",
    "Air Conditioning",
    "Heating",
    "Washer",
    "Dryer",
    "Kitchen",
    "TV",
    "Pet Friendly",
    "Wheelchair Access",
];

export const EditProperty: React.FC = () => {
    const { propertyId } = useParams<{ propertyId: string }>();
    const navigate = useNavigate();
    const { updateProperty, fetchPropertyDetails, currentProperty, properties, loading } = useLandlordStore();
    const { success, error: toastError, warning } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [images, setImages] = useState<string[]>([]);
    const [newLocalImages, setNewLocalImages] = useState<string[]>([]);
    const [amenities, setAmenities] = useState<string[]>([]);
    const [newAmenity, setNewAmenity] = useState("");

    useEffect(() => {
        if (!propertyId) return;

        const loadData = async () => {
            let prop = properties.find((p) => p.id === propertyId) || currentProperty;
            
            if (!prop || prop.id !== propertyId) {
                await fetchPropertyDetails(propertyId);
                prop = useLandlordStore.getState().currentProperty;
            }

            if (prop) {
                setName(prop.name || "");
                setDescription(prop.description || "");
                setImages(prop.images || []);
                setAmenities(prop.amenities || []);
                setContactEmail(prop.contact_email || "");
                setContactPhone(prop.contact_phone || "");
            }
        };

        loadData();
    }, [propertyId, properties, currentProperty, fetchPropertyDetails]);

    const handleSaveChanges = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!propertyId) return;

        setIsSubmitting(true);
        try {
            // In a real scenario, we'd upload newLocalImages to Supabase Storage first
            // and append their returned public URLs to finalImageUrls. 
            // Here, mimicking the setup without mutating backend storage mechanisms.
            const finalImageUrls = [...images, ...newLocalImages];

            await updateProperty(propertyId, {
                name,
                description,
                images: finalImageUrls,
                amenities,
                contact_email: contactEmail,
                contact_phone: contactPhone,
            });

            navigate(-1);
        } catch (err: any) {
            console.error("Save failed:", err);
            toastError('Save failed', 'Could not save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const totalImages = images.length + newLocalImages.length;
        if (totalImages >= 8) {
            warning('Image limit reached', 'You can add a maximum of 8 images.');
            return;
        }

        if (e.target.files) {
            const selectedImages = Array.from(e.target.files).map((f) => URL.createObjectURL(f));
            setNewLocalImages([...newLocalImages, ...selectedImages].slice(0, 8 - images.length));
        }
    };

    const handleRemoveExistingImage = (imageUrl: string) => {
        if (window.confirm("Are you sure you want to remove this image?")) {
            setImages(images.filter((img) => img !== imageUrl));
        }
    };

    const handleRemoveNewImage = (imageUri: string) => {
        setNewLocalImages(newLocalImages.filter((img) => img !== imageUri));
    };

    const handleAddAmenity = () => {
        if (newAmenity.trim() && !amenities.includes(newAmenity.trim())) {
            setAmenities([...amenities, newAmenity.trim()]);
            setNewAmenity("");
        }
    };

    const handlePreloadedAmenityToggle = (amenity: string) => {
        if (amenities.includes(amenity)) {
            setAmenities(amenities.filter((a) => a !== amenity));
        } else {
            setAmenities([...amenities, amenity]);
        }
    };

    if (loading && !name) {
        return <LoadingScreen />;
    }

    const allImages = [...images, ...newLocalImages];

    const inputClass = "w-full px-4 py-3 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow";
    const labelClass = "block text-base font-semibold text-light-text dark:text-dark-text mb-2";
    const sectionClass = "mb-8";

    return (
        <div className="max-w-4xl mx-auto pb-10 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                    type="button"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Edit Property</h1>
            </div>

            <form onSubmit={handleSaveChanges} className="space-y-6 bg-light-card dark:bg-dark-card p-6 md:p-8 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                
                {/* Basic Information */}
                <div className={sectionClass}>
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 pb-2 border-b border-light-border dark:border-dark-border">Basic Information</h2>
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Property Name</label>
                            <input 
                                type="text" 
                                value={name} 
                                onChange={(e) => setName(e.target.value)} 
                                className={inputClass} 
                                required
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Description</label>
                            <textarea 
                                value={description} 
                                onChange={(e) => setDescription(e.target.value)} 
                                rows={4} 
                                className={inputClass} 
                            />
                        </div>
                    </div>
                </div>

                {/* Contact Details */}
                <div className={sectionClass}>
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 pb-2 border-b border-light-border dark:border-dark-border">Contact Details</h2>
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Public Contact Phone</label>
                            <input 
                                type="tel" 
                                value={contactPhone} 
                                onChange={(e) => setContactPhone(e.target.value)} 
                                className={inputClass} 
                                placeholder="e.g., +254 712 345678"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Public Contact Email</label>
                            <input 
                                type="email" 
                                value={contactEmail} 
                                onChange={(e) => setContactEmail(e.target.value)} 
                                className={inputClass} 
                                placeholder="e.g., info@majengoheights.com"
                            />
                        </div>
                    </div>
                </div>

                {/* Images */}
                <div className={sectionClass}>
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 pb-2 border-b border-light-border dark:border-dark-border">
                        Property Images ({allImages.length}/8)
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {allImages.map((uri, idx) => {
                            const isNew = newLocalImages.includes(uri);
                            return (
                                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-light-border dark:border-dark-border group">
                                    <img src={uri} alt={`property-${idx}`} className="w-full h-full object-cover" />
                                    <button 
                                        type="button" 
                                        onClick={() => isNew ? handleRemoveNewImage(uri) : handleRemoveExistingImage(uri)}
                                        className="absolute top-2 right-2 bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                                    >
                                        <X size={20} className="text-red-500" />
                                    </button>
                                </div>
                            );
                        })}
                        
                        {allImages.length < 8 && (
                            <label className="flex items-center justify-center aspect-square rounded-xl border-2 border-dashed border-light-border dark:border-dark-border cursor-pointer hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                                <div className="flex flex-col items-center text-primary dark:text-golden">
                                    <Plus size={32} />
                                </div>
                                <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageChange} />
                            </label>
                        )}
                    </div>
                </div>

                {/* Amenities */}
                <div className={sectionClass}>
                    <h2 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 pb-2 border-b border-light-border dark:border-dark-border">Amenities</h2>
                    <div className="flex flex-wrap gap-2 mb-4">
                        {PRELOADED_AMENITIES.map((amenity) => {
                            const isSelected = amenities.includes(amenity);
                            return (
                                <button
                                    key={amenity}
                                    type="button"
                                    onClick={() => handlePreloadedAmenityToggle(amenity)}
                                    className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                                        isSelected 
                                        ? 'bg-primary/20 border-primary text-primary dark:bg-golden/20 dark:border-golden dark:text-golden' 
                                        : 'bg-light-card border-light-border text-light-text dark:bg-dark-card dark:border-dark-border dark:text-dark-text'
                                    }`}
                                >
                                    {amenity}
                                </button>
                            );
                        })}
                    </div>
                    
                    <div className="flex items-center gap-3 mt-4">
                        <input 
                            type="text" 
                            className={inputClass} 
                            placeholder="Add custom amenity" 
                            value={newAmenity}
                            onChange={(e) => setNewAmenity(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddAmenity();
                                }
                            }}
                        />
                        <button 
                            type="button" 
                            onClick={handleAddAmenity}
                            className="p-3.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
                        >
                            <Plus size={24} />
                        </button>
                    </div>
                </div>

                <div className="pt-6">
                    <button 
                        type="submit" 
                        disabled={isSubmitting || loading}
                        className="w-full py-4 text-center bg-primary text-white rounded-xl font-bold text-lg hover:opacity-90 disabled:opacity-50 transition-opacity shadow-lg shadow-primary/20 flex items-center justify-center gap-3"
                    >
                        {(isSubmitting || loading) && <Loader2 className="animate-spin" size={24} />}
                        Save Changes
                    </button>
                </div>

            </form>
        </div>
    );
};
